<script lang="ts">
  import FizzStyles from "@shared/svelte/Components/FizzStyles.svelte";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import Modal from "@shared/svelte/Components/Modal.svelte";
  import DistributorFulfillmentSection from "@retailer/svelte/Cart/Page/OrderFulfillment/DistributorFulfillmentSection.svelte";
  import Flash from "@shared/services/flashv2.js";

  // services
  import { EventTracking } from "@shared/services/event_tracking/events";

  // types
  import type { FulfillmentInformationType, OrderPaymentMethod } from "@shared/types/Cart";

  export let fulfillmentInformationByDistributor: FulfillmentInformationType[];
  export let cartSubmit: () => Promise<any>;
  export let fulfillmentUpdate: (updateProducts: any) => Promise<any>;
  export let batchUpdateOrders: (orderData: OrderPaymentMethod[]) => Promise<any>;
  export let closeModal: () => void;
  export let OHLQOrderPaymentMethodsEnabled: boolean;
  export let distributorUsesWineCellar: boolean = false;

  let submitting = false;

  const createOrderFulfillmentHash = () => {
    return fulfillmentInformationByDistributor.reduce((hash, info) => {
      info.order_warehouses.forEach(orderWarehouse => {
        hash[orderWarehouse.id] = {
          order_warehouse_id: orderWarehouse.id,
          fulfillment_date: orderWarehouse.fulfillment_date,
        };
        if (OHLQOrderPaymentMethodsEnabled && info.show_fulfillment_times) {
          hash[orderWarehouse.id] = {
            ...hash[orderWarehouse.id],
            fulfillment_time: orderWarehouse.fulfillment_time,
          };
        }
      });
      return hash;
    }, {});
  };
  let orderFulfillmentHash = createOrderFulfillmentHash();

  const createOrderPaymentMethodHash = () => {
    return fulfillmentInformationByDistributor.reduce((hash, info) => {
      hash[info.order.id] = {
        order_id: info.order.id,
        intended_payment_method: info.order.intended_payment_method,
      };
      return hash;
    }, {});
  };

  let orderPaymentMethodHash = createOrderPaymentMethodHash();

  const orderPaymentMethodUpdate = async () => {
    const orderPaymentMethods = Object.values(orderPaymentMethodHash) as OrderPaymentMethod[];
    const hasPaymentMethods = Object.values(fulfillmentInformationByDistributor).some(
      info => info.show_payment_methods,
    );
    if (OHLQOrderPaymentMethodsEnabled && hasPaymentMethods) {
      await batchUpdateOrders(orderPaymentMethods);
    }
  };

  const normalizeDate = dateStr => {
    const isISO = /^\d{4}-\d{2}-\d{2}$/.test(dateStr);

    let date;
    if (isISO) {
      // If already in yyyy-mm-dd format, just return as-is
      return dateStr;
    } else {
      // Append current year and parse the string
      const fullStr = `${dateStr} ${new Date().getFullYear()}`;
      date = new Date(fullStr);

      if (isNaN(date)) {
        throw new Error("Invalid date format");
      }
    }

    // Get components in UTC to avoid time zone shift
    const yyyy = date.getUTCFullYear();
    const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(date.getUTCDate()).padStart(2, "0");

    return `${yyyy}-${mm}-${dd}`;
  };

  const submit = async () => {
    submitting = true;
    const orderFulfillment = Object.values(orderFulfillmentHash) as Array<{
      fulfillment_date: string;
      [key: string]: any;
    }>;
    orderFulfillment.forEach(order => {
      order.fulfillment_date = normalizeDate(order.fulfillment_date);
    });

    try {
      await Promise.all([
        orderPaymentMethodUpdate(),
        fulfillmentUpdate(orderFulfillment),
        cartSubmit(),
      ]);
    } catch (err) {
      if (err?.data instanceof Array) {
        err?.data.forEach((message: string) => {
          Flash.now({ type: "error", compact: true, message });
        });
      } else {
        Flash.now({ type: "error", compact: true, message: "An unexpected error occurred." });
      }
    } finally {
      submitting = false;
    }
  };

  const onDateSelect = (orderWarehouseId: number, selectedDate: Date) => {
    orderFulfillmentHash[orderWarehouseId]["fulfillment_date"] = selectedDate;
  };

  const onTimeSelect = (orderWarehouseId: number, selectedTime: string) => {
    orderFulfillmentHash[orderWarehouseId]["fulfillment_time"] = selectedTime;
  };

  const onPaymentMethodSelect = (orderId: number, selectedPaymentMethod: string) => {
    orderPaymentMethodHash[orderId]["intended_payment_method"] = selectedPaymentMethod;

    const paymentMethodToTrack = selectedPaymentMethod.toLowerCase().replace(" ", "_");
    EventTracking.trackClick(
      "cart_order_fulfillment_payment_method_radio_button_" + paymentMethodToTrack,
    );
  };

  const modalTitle = () => {
    if (OHLQOrderPaymentMethodsEnabled) {
      const orderWarehouses = fulfillmentInformationByDistributor
        .map(i => i.order_warehouses)
        .flat();
      const warehousePickups = orderWarehouses.filter(ow => ow.warehouse_id);
      const warehouseDeliveries = orderWarehouses.filter(ow => !ow.warehouse_id);
      const hasPickup = warehousePickups.length > 0;
      const hasDelivery = warehouseDeliveries.length > 0;
      if (hasPickup && hasDelivery) {
        return "Schedule your Delivery and Pickup";
      } else if (hasPickup) {
        return `Schedule your Pickup${warehousePickups.length > 1 ? "s" : ""}`;
      } else {
        return `Schedule your Deliver${warehouseDeliveries.length > 1 ? "ies" : "y"}`;
      }
    }
    return "Select Dates";
  };
</script>

<FizzStyles>
  <Modal
    closeOnBackgroundClick={false}
    show={true}
    size={OHLQOrderPaymentMethodsEnabled ? "medium" : "small"}
    onClose={closeModal}
    fizzModal={true}
    overflowY={"auto"}
  >
    <header
      class="fizz-modal-header fizz-space-between"
      class:fizz-border-bottom={!OHLQOrderPaymentMethodsEnabled}
      class:fizz-stack-16={!OHLQOrderPaymentMethodsEnabled}
    >
      <div
        class:fizz-heading-4={!OHLQOrderPaymentMethodsEnabled}
        class:fizz-text-large={OHLQOrderPaymentMethodsEnabled}
        class:fizz-text-strong={OHLQOrderPaymentMethodsEnabled}>{modalTitle()}</div
      >
      <div
        class="fizz-modal-toggle fizz-modal-close"
        data-testid="action-close"
        on:click={closeModal}
        disabled={submitting}
      >
        <Icon type="X" extraClassNames="fizz-text-brand cursor-pointer" height="16" width="16" />
      </div>
    </header>
    <section
      class:fizz-modal-main={!OHLQOrderPaymentMethodsEnabled}
      class:fizz-border-bottom={!OHLQOrderPaymentMethodsEnabled}
      class:fizz-flow-16={!OHLQOrderPaymentMethodsEnabled}
      class:fizz-inset-horizontal-24={OHLQOrderPaymentMethodsEnabled}
    >
      {#each fulfillmentInformationByDistributor as fulfillmentInformation}
        <DistributorFulfillmentSection
          {fulfillmentInformation}
          {onDateSelect}
          {onTimeSelect}
          {onPaymentMethodSelect}
          {OHLQOrderPaymentMethodsEnabled}
          {distributorUsesWineCellar}
        />
      {/each}
    </section>
    <footer
      class="fizz-modal-footer fizz-flex-row fizz-justify-end"
      class:fizz-inset-square-16={!OHLQOrderPaymentMethodsEnabled}
      class:fizz-flow-24={OHLQOrderPaymentMethodsEnabled}
    >
      {#if OHLQOrderPaymentMethodsEnabled}
        <button
          class="fizz-button"
          data-testid="action-cancel"
          on:click={closeModal}
          style="margin-bottom: 0;"
        >
          Cancel
        </button>
      {/if}
      <button
        class="fizz-button-secondary"
        data-testid="action-submit"
        on:click={submit}
        disabled={submitting}
      >
        {#if submitting}
          Processing...
        {:else if OHLQOrderPaymentMethodsEnabled}
          Send order
        {:else}
          Confirm dates and send all orders
        {/if}
      </button>
    </footer>
  </Modal>
</FizzStyles>
